import * as i0 from '@angular/core';
import { InjectionToken, Directive, Input, Component, Inject, Optional, APP_BOOTSTRAP_LISTENER, NgModule } from '@angular/core';
import * as i7 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import * as i3 from '@angular/forms';
import { Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, of } from 'rxjs';
import * as i2 from '@angular/router';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { takeUntil, tap } from 'rxjs/operators';
import * as i1$1 from '@aihub/core/authorization';
import { AuthService, AiHubAuthModule } from '@aihub/core/authorization';
import * as i4 from '@angular/material/grid-list';
import { MatGridListModule } from '@angular/material/grid-list';
import * as i5 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i6 from '@angular/material/form-field';
import { MatFormFieldModule } from '@angular/material/form-field';
import * as i9 from '@angular/material/input';
import { MatInputModule } from '@angular/material/input';

function autoLoginFactory() {
    let isAutoLogin = false;
    /** get value from session storage */
    if (sessionStorage.getItem('autoLogin')) {
        isAutoLogin = sessionStorage.getItem('autoLogin') !== 'false';
    }
    return isAutoLogin;
}

const AuthRedirectUri = new InjectionToken('path to navigate if logged in');
const AutoLoginToken = new InjectionToken('Auto Login', {
    providedIn: 'root',
    factory: () => autoLoginFactory(),
});

const mailTohref = 'mailto:globaldeloitteai-onboarding@deloitte.com?subject=Signup%20Request';

class AiHubLoginButtonIdentityDirective {
    constructor(el, translate, ssoStrategy) {
        this.el = el;
        this.translate = translate;
        this.ssoStrategy = ssoStrategy;
        this.signInLabel = 'Sign in ';
        this.isValidIdentity = false;
        this.corporateProviderLabel = 'corporate account ';
        this.validClients = [
            { key: 'google', name: 'google' },
            { key: 'email', name: 'email and password' },
            { key: 'iframe', name: 'iframe' },
        ];
    }
    ngAfterViewInit() {
        this.hostEl = this.el.nativeElement;
        if (this.isCorporateProvider()) {
            this.renderButton(this.corporateProviderLabel);
        }
        else {
            this.validClients.forEach((client) => {
                if (this.identity.toLowerCase().includes(client.key)) {
                    this.renderButton(client.name);
                }
            });
        }
        if (!this.isValidIdentity) {
            this.hideButton();
        }
    }
    isCorporateProvider() {
        const corporateTokens = this.ssoStrategy.identifier;
        const params = this.identity.split('.');
        const signInMethod = params[0];
        const provider = params[params.length - 1];
        const contains = corporateTokens.includes(signInMethod);
        this.corporateProviderLabel = this.corporateProviderLabel.concat(`(${provider})`);
        return !!contains;
    }
    renderButton(name) {
        const labelEl = this.hostEl.querySelector('span');
        labelEl.innerText = this.signInLabel + this.toTitleCase(name);
        this.isValidIdentity = true;
    }
    hideButton() {
        this.hostEl.classList.value = '';
        this.hostEl.classList.add('d-none');
    }
    toTitleCase(str) {
        return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    }
}
AiHubLoginButtonIdentityDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubLoginButtonIdentityDirective, deps: [{ token: i0.ElementRef }, { token: i1.TranslateService }, { token: i1$1.SSOStrategy }], target: i0.ɵɵFactoryTarget.Directive });
AiHubLoginButtonIdentityDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: AiHubLoginButtonIdentityDirective, selector: "[aihubLoginIdentity]", inputs: { identity: ["aihubLoginIdentity", "identity"] }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubLoginButtonIdentityDirective, decorators: [{
            type: Directive,
            args: [{
                    // tslint:disable-next-line: directive-selector
                    selector: '[aihubLoginIdentity]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1.TranslateService }, { type: i1$1.SSOStrategy }]; }, propDecorators: { identity: [{
                type: Input,
                args: ['aihubLoginIdentity']
            }] } });

class LoginComponent {
    constructor(authRedirect, autoLogin, authService, router, activatedRoute, cdRef, formBuilder, iframeStrategy) {
        this.authRedirect = authRedirect;
        this.autoLogin = autoLogin;
        this.authService = authService;
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.cdRef = cdRef;
        this.formBuilder = formBuilder;
        this.iframeStrategy = iframeStrategy;
        this.isUserLoggedIn = false;
        this.isLoading = false;
        this.loginAttemps = 0;
        this.providers = [];
        this.isEmailLogin = false;
        this.destroy$ = new Subject();
        this.mailTohref = mailTohref;
        this.providers = this.authService.loginProviders;
    }
    ngOnInit() {
        this.isLoading = true;
        this.createSignInForm();
        this.signInForm.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => {
            this.authService.emailLoginArguments$.next(val);
        });
        this.loginAttemps =
            parseInt(sessionStorage.getItem('login-attempts'), 10) || 0;
        this.authService.isAuthorized().pipe(takeUntil(this.destroy$)).subscribe((authorized) => {
            if (authorized) {
                sessionStorage.removeItem('login-attempts');
                this.isUserLoggedIn = true;
                this.redirect();
                return;
            }
            // Removed autologin functionality because
            // multiple providers are now allowed
            this.isLoading = false;
            this.cdRef.markForCheck();
        });
    }
    doLogin(provider) {
        this.cdRef.markForCheck();
        if (this.authService.isEmailAndPasswordLogin(provider)) {
            this.isEmailLogin = true;
        }
        else {
            this.authService.login(provider);
        }
        sessionStorage.setItem('login-attempts', (this.loginAttemps + 1).toString());
    }
    loginWithEmailAndPassword() {
        this.authService.login('emailandpassword').then(() => this.redirect());
    }
    createSignInForm() {
        this.signInForm = this.formBuilder.group({
            /** email control */
            email: this.formBuilder.control(null, [
                Validators.required,
                Validators.email,
            ]),
            /** password control */
            password: this.formBuilder.control(null, [Validators.required]),
        });
    }
    switchToSelectMode() {
        this.isEmailLogin = false;
    }
    redirect() {
        const routeData = this.authRedirect(this.activatedRoute);
        this.router.navigate([routeData.path], {
            queryParams: routeData.queryParams,
        });
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
        this.destroy$ = null;
    }
}
LoginComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LoginComponent, deps: [{ token: AuthRedirectUri }, { token: AutoLoginToken, optional: true }, { token: i1$1.AuthService }, { token: i2.Router }, { token: i2.ActivatedRoute }, { token: i0.ChangeDetectorRef }, { token: i3.FormBuilder }, { token: i1$1.IframeStrategy }], target: i0.ɵɵFactoryTarget.Component });
LoginComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: LoginComponent, selector: "aihub-login-page", ngImport: i0, template: "<div class=\"padded-container\" *ngIf=\"!isEmailLogin\">\r\n  <mat-grid-list cols=\"2\">\r\n    <mat-grid-tile>\r\n      <div class=\"input-container\">\r\n        <ng-container *ngFor=\"let provider of providers\">\r\n          <button [aihubLoginIdentity]=\"provider\" mat-raised-button class=\"my-class\" color=\"primary\"\r\n            (click)=\"doLogin(provider)\" [disabled]=\"isLoading\">\r\n            <span id=\"login-btn-label\">{{\r\n              \"login.card-btn-text\" | translate\r\n            }}</span>\r\n          </button>\r\n        </ng-container>\r\n      </div>\r\n    </mat-grid-tile>\r\n  </mat-grid-list>\r\n</div>\r\n\r\n<div class=\"padded-container light\" *ngIf=\"isEmailLogin\">\r\n  <mat-grid-list cols=\"1\" rowHeight=\"2:1\">\r\n    <form [formGroup]=\"signInForm\">\r\n    <mat-grid-tile>\r\n      <div class=\"input-container\">\r\n        <div class=\"mat-display-1\">\r\n          <b>{{ \"Sign In\" | translate }}</b>\r\n        </div>\r\n\r\n          <span class=\"fixed-label\">{{ \"Email\" | translate }}</span>\r\n          <mat-form-field floatLabel=\"always\" appearance=\"outline\" color=\"primary\">\r\n            <input matInput required formControlName=\"email\" type=\"email\" id=\"email\" />\r\n          </mat-form-field>\r\n\r\n          <span class=\"fixed-label\">{{ \"Password\" | translate }}</span>\r\n          <mat-form-field floatLabel=\"always\" appearance=\"outline\" color=\"primary\">\r\n            <input matInput required formControlName=\"password\" type=\"password\" id=\"pwd\" />\r\n          </mat-form-field>\r\n\r\n          <button mat-raised-button class=\"email-button\" color=\"primary\" (click)=\"loginWithEmailAndPassword()\"\r\n            type=\"submit\">\r\n            {{ \"Sign In\" | translate }}\r\n          </button>\r\n\r\n        <div class=\"mat-body-1\">\r\n          <div >\r\n            <a [href]=\"mailTohref\">Contact us</a> to sign up\r\n          </div>\r\n          <div style=\"margin-top: 10px; display: flex; justify-content: center;\">\r\n            {{ \"or\" | translate }}\r\n          </div>\r\n        </div>\r\n        <div style=\"margin-top: 10px\">\r\n          <div (click)=\"switchToSelectMode()\" class=\"mat-body-1\">\r\n            <a>{{ \"Change sign in method\" | translate }}</a>\r\n          </div>\r\n        </div>\r\n      </div>\r\n    </mat-grid-tile>\r\n    </form>\r\n  </mat-grid-list>\r\n</div>", styles: [":host{background:#000000}.padded-container{background-color:inherit;padding:0 1.25rem;height:100%}.input-container{width:100%;max-height:70%;display:flex;flex-direction:column;align-items:center;justify-content:flex-end;padding:1rem;margin-bottom:1rem}.input-container button{margin-bottom:1rem;width:80%;min-width:18.75rem;min-height:5rem;background-color:#2b7cb0;font-size:1.2rem}.input-container .email-button{width:40%}.input-container mat-form-field{width:40%;min-width:18.75rem}.input-container .fixed-label{width:40%}.input-container a{color:#2b7cb0}.input-container a:hover{cursor:pointer;color:#2b7cb0}.light{background-color:#fff}\n"], components: [{ type: i4.MatGridList, selector: "mat-grid-list", inputs: ["cols", "gutterSize", "rowHeight"], exportAs: ["matGridList"] }, { type: i4.MatGridTile, selector: "mat-grid-tile", inputs: ["rowspan", "colspan"], exportAs: ["matGridTile"] }, { type: i5.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }, { type: i6.MatFormField, selector: "mat-form-field", inputs: ["color", "appearance", "hideRequiredMarker", "hintLabel", "floatLabel"], exportAs: ["matFormField"] }], directives: [{ type: i7.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i7.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: AiHubLoginButtonIdentityDirective, selector: "[aihubLoginIdentity]", inputs: ["aihubLoginIdentity"] }, { type: i3.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { type: i3.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { type: i3.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { type: i9.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { type: i3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: i3.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i3.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }], pipes: { "translate": i1.TranslatePipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LoginComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aihub-login-page', template: "<div class=\"padded-container\" *ngIf=\"!isEmailLogin\">\r\n  <mat-grid-list cols=\"2\">\r\n    <mat-grid-tile>\r\n      <div class=\"input-container\">\r\n        <ng-container *ngFor=\"let provider of providers\">\r\n          <button [aihubLoginIdentity]=\"provider\" mat-raised-button class=\"my-class\" color=\"primary\"\r\n            (click)=\"doLogin(provider)\" [disabled]=\"isLoading\">\r\n            <span id=\"login-btn-label\">{{\r\n              \"login.card-btn-text\" | translate\r\n            }}</span>\r\n          </button>\r\n        </ng-container>\r\n      </div>\r\n    </mat-grid-tile>\r\n  </mat-grid-list>\r\n</div>\r\n\r\n<div class=\"padded-container light\" *ngIf=\"isEmailLogin\">\r\n  <mat-grid-list cols=\"1\" rowHeight=\"2:1\">\r\n    <form [formGroup]=\"signInForm\">\r\n    <mat-grid-tile>\r\n      <div class=\"input-container\">\r\n        <div class=\"mat-display-1\">\r\n          <b>{{ \"Sign In\" | translate }}</b>\r\n        </div>\r\n\r\n          <span class=\"fixed-label\">{{ \"Email\" | translate }}</span>\r\n          <mat-form-field floatLabel=\"always\" appearance=\"outline\" color=\"primary\">\r\n            <input matInput required formControlName=\"email\" type=\"email\" id=\"email\" />\r\n          </mat-form-field>\r\n\r\n          <span class=\"fixed-label\">{{ \"Password\" | translate }}</span>\r\n          <mat-form-field floatLabel=\"always\" appearance=\"outline\" color=\"primary\">\r\n            <input matInput required formControlName=\"password\" type=\"password\" id=\"pwd\" />\r\n          </mat-form-field>\r\n\r\n          <button mat-raised-button class=\"email-button\" color=\"primary\" (click)=\"loginWithEmailAndPassword()\"\r\n            type=\"submit\">\r\n            {{ \"Sign In\" | translate }}\r\n          </button>\r\n\r\n        <div class=\"mat-body-1\">\r\n          <div >\r\n            <a [href]=\"mailTohref\">Contact us</a> to sign up\r\n          </div>\r\n          <div style=\"margin-top: 10px; display: flex; justify-content: center;\">\r\n            {{ \"or\" | translate }}\r\n          </div>\r\n        </div>\r\n        <div style=\"margin-top: 10px\">\r\n          <div (click)=\"switchToSelectMode()\" class=\"mat-body-1\">\r\n            <a>{{ \"Change sign in method\" | translate }}</a>\r\n          </div>\r\n        </div>\r\n      </div>\r\n    </mat-grid-tile>\r\n    </form>\r\n  </mat-grid-list>\r\n</div>", styles: [":host{background:#000000}.padded-container{background-color:inherit;padding:0 1.25rem;height:100%}.input-container{width:100%;max-height:70%;display:flex;flex-direction:column;align-items:center;justify-content:flex-end;padding:1rem;margin-bottom:1rem}.input-container button{margin-bottom:1rem;width:80%;min-width:18.75rem;min-height:5rem;background-color:#2b7cb0;font-size:1.2rem}.input-container .email-button{width:40%}.input-container mat-form-field{width:40%;min-width:18.75rem}.input-container .fixed-label{width:40%}.input-container a{color:#2b7cb0}.input-container a:hover{cursor:pointer;color:#2b7cb0}.light{background-color:#fff}\n"] }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [AuthRedirectUri]
                    }] }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [AutoLoginToken]
                    }] }, { type: i1$1.AuthService }, { type: i2.Router }, { type: i2.ActivatedRoute }, { type: i0.ChangeDetectorRef }, { type: i3.FormBuilder }, { type: i1$1.IframeStrategy }];
    } });

/**
 */
function LoginRouteFactory(router) {
    return () => {
        const routes = router.config;
        let insertAt = 0;
        for (let i = routes.length - 1; i > -1; i--) {
            const config = routes[i];
            if (config.path === '**') {
                insertAt = i;
            }
        }
        const loginRoute = {
            path: 'login',
            component: LoginComponent
        };
        router.config.splice(insertAt, 0, loginRoute);
    };
}

class LoginPageRoutingModule {
}
LoginPageRoutingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LoginPageRoutingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LoginPageRoutingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LoginPageRoutingModule, exports: [RouterModule] });
LoginPageRoutingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LoginPageRoutingModule, providers: [{
            provide: APP_BOOTSTRAP_LISTENER,
            multi: true,
            useFactory: LoginRouteFactory,
            deps: [Router]
        }], imports: [RouterModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LoginPageRoutingModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [{
                            provide: APP_BOOTSTRAP_LISTENER,
                            multi: true,
                            useFactory: LoginRouteFactory,
                            deps: [Router]
                        }],
                    exports: [RouterModule],
                }]
        }] });

function autoLoginResolver(allways = false) {
    return () => {
        const params = window.location.search.substr(1).split('&');
        const hasAutoLogin = params.some((touple) => {
            const [key] = touple.split('=');
            return key === 'autologin';
        });
        return allways || hasAutoLogin;
    };
}

function redirectDataResolverFactory(redirectFactory) {
    return (authService) => redirectFactory ? redirectFactory : authService.resolveRedirectData;
}
const MaterialComponents = [
    MatButtonModule,
    MatGridListModule,
    MatFormFieldModule,
    MatInputModule,
];
class AiHubLoginPageModule {
    constructor(authService, autoLogin) {
        this.authService = authService;
        /**
         * write initial value for auto login in session storage
         */
        sessionStorage.setItem('autoLogin', autoLogin ? 'true' : 'false');
        /**
         * register logout hook which sets autologin to false
         * if the user logged out
         */
        this.authService.addLogoutHook(of(true).pipe(tap(() => sessionStorage.setItem('autoLogin', 'false'))));
    }
    static forRoot(autoLogin = false, afterLogin) {
        return {
            ngModule: AiHubLoginPageModule,
            providers: [
                {
                    provide: AuthRedirectUri,
                    useFactory: redirectDataResolverFactory(afterLogin),
                    deps: [AuthService]
                },
                {
                    provide: 'AutoLoginEnabled', useFactory: autoLoginResolver(autoLogin),
                    deps: [ActivatedRoute]
                },
            ]
        };
    }
}
AiHubLoginPageModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubLoginPageModule, deps: [{ token: i1$1.AuthService }, { token: 'AutoLoginEnabled' }], target: i0.ɵɵFactoryTarget.NgModule });
AiHubLoginPageModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubLoginPageModule, declarations: [LoginComponent,
        AiHubLoginButtonIdentityDirective], imports: [FormsModule,
        ReactiveFormsModule,
        AiHubAuthModule,
        CommonModule,
        LoginPageRoutingModule,
        TranslateModule, MatButtonModule,
        MatGridListModule,
        MatFormFieldModule,
        MatInputModule] });
AiHubLoginPageModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubLoginPageModule, imports: [[
            FormsModule,
            ReactiveFormsModule,
            AiHubAuthModule,
            CommonModule,
            LoginPageRoutingModule,
            TranslateModule,
            MaterialComponents
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubLoginPageModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        LoginComponent,
                        AiHubLoginButtonIdentityDirective
                    ],
                    imports: [
                        FormsModule,
                        ReactiveFormsModule,
                        AiHubAuthModule,
                        CommonModule,
                        LoginPageRoutingModule,
                        TranslateModule,
                        MaterialComponents
                    ],
                    exports: []
                }]
        }], ctorParameters: function () {
        return [{ type: i1$1.AuthService }, { type: undefined, decorators: [{
                        type: Inject,
                        args: ['AutoLoginEnabled']
                    }] }];
    } });

/**
 * Generated bundle index. Do not edit.
 */

export { AiHubLoginPageModule, redirectDataResolverFactory };
